<template>
  <dashboard-widget title="OUTSTANDING" no-zoom @delete="$emit('delete','DashboardOutstanding')" :width="width" :enableDelete="enableDelete">
    <v-menu left :close-on-content-click="false" max-width="450">
      <template v-slot:activator="{on}">
        <v-btn v-if="!enableDelete" icon small v-on="on" style="position:absolute;right:10px;top:10px" @click="loadSettings()">
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
      <v-card>
        <v-container class="pt-8 px-4">
          <v-row>
            <v-col>
              <outlined-block title="Include notifications for">
                <v-card flat class="ma-2">
                  <v-container>
                    <v-row>
                      <v-col cols="5">
                        <v-switch v-model="outstandingSettings.reporting" label="Reporting" dense hide-details @change="saveOutstandingSettings"/>
                      </v-col>
                      <v-col cols="5">
                        <v-tooltip bottom  max-width="300" open-delay="1000">
                          <template v-slot:activator="{on}">
                            <div v-on="on">
                              <decimal-number-input label="Advance warning" v-model="outstandingSettings.reportingWarningDays" prefix="" suffix="days" :precision="0" v-on="on" integer :max="30" hide-details class="mt-2" @change="saveOutstandingSettings"/>
                            </div>
                          </template>
                          How many days before CRA Excise returns and other government reports are due should you be warned,
                          if the returns have not already been filed.
                        </v-tooltip>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="4">
                        <v-switch v-model="outstandingSettings.maintenance" label="Maintenance" dense hide-details @change="saveOutstandingSettings"/>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="4">
                        <v-switch v-model="outstandingSettings.production" label="Production" dense hide-details @change="saveOutstandingSettings"/>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="4">
                        <v-switch v-model="outstandingSettings.inventory" label="Inventory" dense hide-details @change="saveOutstandingSettings"/>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="4">
                        <v-switch v-model="outstandingSettings.sales" label="Sales" dense hide-details @change="saveOutstandingSettings"/>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="4">
                        <v-switch v-model="outstandingSettings.purchasing" label="Purchasing" dense hide-details @change="saveOutstandingSettings"/>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </outlined-block>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <outlined-block title="Send email notifications">
                <v-row class="mt-2 mx-0">
                  <v-col cols="12" md="7">
                    <date-picker v-model="next" label="First notification" date-only :disabled="frequency===0" hide-details/>
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-autocomplete dense label="Not more than every" v-model="frequency" :items="days" outlined hide-details
                                    :suffix="frequency ? 'days':''"/>
                  </v-col>
                </v-row>
                <v-row class="mx-0 mb-1">
                  <v-col align="right">
                    <v-btn color="primary" small @click="saveSettings()" :disabled="!dirty">Save</v-btn>
                  </v-col>
                </v-row>
              </outlined-block>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-menu>

    <v-card flat class="mt-2">
      <v-list dense class="text-body-2 overflow-y" height="250" v-show="!loading">
        <v-list-item v-if="!outstanding">Nothing outstanding</v-list-item>
        <v-list-item v-if="exciseDue" :to="{name:'ReportsView',params:{back:[{text:'Dashboard',to:{name:'Dashboard'}}]},query:{to:'reports/excise/ReportsExciseWarehouse'}}"><v-icon>mdi-chevron-right</v-icon>Excise reports to file</v-list-item>

        <component :is="province" v-model="provincialOutstanding"/>

        <v-list-item v-if="poRequiringApproval" :to="{name:'PurchasingPurchaseOrders',query:{approved:'0'}}"><v-icon>mdi-chevron-right</v-icon> {{poRequiringApproval}} purchase order{{poRequiringApproval>1?'s':''}} require{{poRequiringApproval===1?'s':''}} approval</v-list-item>

        <v-list-item v-if="tankMaintenanceDue" :to="{name:'EquipmentTanks',query:{maintenanceDue:'1'}}"><v-icon>mdi-chevron-right</v-icon>{{tankMaintenanceDue}} tank{{tankMaintenanceDue>1?'s':''}} require{{tankMaintenanceDue>1?'':'s'}} maintenance</v-list-item>
        <v-list-item v-if="fermenterMaintenanceDue" :to="{name:'EquipmentFermenters',query:{maintenanceDue:'1'}}"><v-icon>mdi-chevron-right</v-icon>{{fermenterMaintenanceDue}} fermenter{{fermenterMaintenanceDue>1?'s':''}} require{{fermenterMaintenanceDue>1?'':'s'}} maintenance</v-list-item>
        <v-list-item v-if="stillMaintenanceDue" :to="{name:'EquipmentStills',query:{maintenanceDue:'1'}}"><v-icon>mdi-chevron-right</v-icon>{{stillMaintenanceDue}} still{{stillMaintenanceDue>1?'s':''}} require{{stillMaintenanceDue>1?'':'s'}} maintenance</v-list-item>
        <v-list-item v-if="equipmentMaintenanceDue" :to="{name:'EquipmentOther',query:{maintenanceDue:'1'}}"><v-icon>mdi-chevron-right</v-icon>{{equipmentMaintenanceDue}} other equipment require{{equipmentMaintenanceDue>1?'':'s'}} maintenance</v-list-item>

        <v-list-item v-if="salesOrdersOpen" :to="{name:'SalesSalesOrders',query:{open:'1',due:'1'}}"><v-icon>mdi-chevron-right</v-icon> {{salesOrdersOpen}} open and due sales order{{salesOrdersOpen>1?'s':''}}</v-list-item>
        <v-list-item v-if="shipmentsDraft" :to="{name:'SalesShipments',query:{draft:'1'}}"><v-icon>mdi-chevron-right</v-icon> {{shipmentsDraft}} draft shipment{{shipmentsDraft>1?'s':''}}</v-list-item>
        <v-list-item v-if="receiptsDraft" :to="{name:'PurchasingInventoryReceipts',query:{draft:'1'}}"><v-icon>mdi-chevron-right</v-icon> {{receiptsDraft}} draft inventory receipt{{receiptsDraft>1?'s':''}}</v-list-item>
        <v-list-item v-if="receiptsPending" :to="{name:'PurchasingInventoryReceipts',query:{pending:'1'}}"><v-icon>mdi-chevron-right</v-icon> {{receiptsPending}} inventory receipt{{receiptsPending>1?'s':''}} pending</v-list-item>
        <v-list-item v-if="recurringTransactionsReminders" :to="{name:'RecurringTransactions',query:{reminders:'1',scheduleType:'Reminder'}}"><v-icon>mdi-chevron-right</v-icon> {{recurringTransactionsReminders}} recurring transaction{{recurringTransactionsReminders===1?' is':'s are'}}  due</v-list-item>

        <v-list-item v-if="distillationsIncomplete" :to="{name:'ProductionDistilling',query:{completeStatus:'0'}}"><v-icon>mdi-chevron-right</v-icon> {{distillationsIncomplete}} incomplete distillation{{distillationsIncomplete>1?'s':''}}</v-list-item>
        <v-list-item v-if="mashesIncomplete" :to="{name:'ProductionMashing',query:{completeStatus:'0'}}"><v-icon>mdi-chevron-right</v-icon> {{mashesIncomplete}} incomplete mash{{mashesIncomplete>1?'es':''}}</v-list-item>
        <v-list-item v-if="bottlingsIncomplete" :to="{name:'ProductionBottling',query:{completeStatus:'0'}}"><v-icon>mdi-chevron-right</v-icon> {{bottlingsIncomplete}} incomplete bottling{{bottlingsIncomplete>1?'s':''}}</v-list-item>
        <v-list-item v-if="blendsIncomplete" :to="{name:'ProductionBlending',query:{completeStatus:'0'}}"><v-icon>mdi-chevron-right</v-icon> {{blendsIncomplete}} incomplete blend{{blendsIncomplete>1?'s':''}}</v-list-item>
        <v-list-item v-if="assembliesIncomplete" :to="{name:'ProductionAssembly',query:{completeStatus:'0'}}"><v-icon>mdi-chevron-right</v-icon> {{assembliesIncomplete}} incomplete {{assembliesIncomplete>1?'assemblies':'assembly'}}</v-list-item>
        <v-list-item v-if="itemsLowInventory" :to="{name:'InventoryItems',query:{lowInventory:'1'}}"><v-icon>mdi-chevron-right</v-icon> {{itemsLowInventory}} item{{itemsLowInventory>1?'s':''}} with low inventory</v-list-item>
        <v-list-item v-if="spiritsLowInventory" :to="{name:'Spirits',query:{lowInventory:'1'}}"><v-icon>mdi-chevron-right</v-icon> {{spiritsLowInventory}} spirit{{spiritsLowInventory>1?'s':''}} with low inventory</v-list-item>
        <v-list-item v-if="winesLowInventory" :to="{name:'Wine',query:{lowInventory:'1'}}"><v-icon>mdi-chevron-right</v-icon> {{winesLowInventory}} wine{{winesLowInventory>1?'s':''}} with low inventory</v-list-item>
        <v-list-item v-if="inBondProductsLowInventory" :to="{name:'InventoryProducts',query:{inBondLowInventory:'1'}}"><v-icon>mdi-chevron-right</v-icon> {{inBondProductsLowInventory}} in-bond product{{inBondProductsLowInventory>1?'s':''}} with low inventory</v-list-item>
        <v-list-item v-if="dutyPaidProductsLowInventory" :to="{name:'InventoryProducts',query:{dutyPaidLowInventory:'1'}}"><v-icon>mdi-chevron-right</v-icon> {{dutyPaidProductsLowInventory}} duty-paid product{{dutyPaidProductsLowInventory>1?'s':''}} with low inventory</v-list-item>
      </v-list>
    </v-card>
  </dashboard-widget>
</template>

<script>
import {add,format,lastDayOfMonth} from 'date-fns';

export default {
  name: 'DashboardOutstanding',
  props: {
    width: {type:Number, required:false, default:290},
    enableDelete: {type: Boolean, required:false, default:false}
  },
  data() {
    return {
      loading: true,
      poRequiringApproval: 0,
      shipmentsDraft: 0,
      salesOrdersOpen: 0,
      receiptsDraft: 0,
      receiptsPending: 0,
      recurringTransactionsReminders: 0,
      itemsLowInventory: 0,
      spiritsLowInventory: 0,
      winesLowInventory: 0,
      inBondProductsLowInventory: 0,
      dutyPaidProductsLowInventory: 0,
      mashesIncomplete: 0,
      distillationsIncomplete: 0,
      bottlingsIncomplete: 0,
      assembliesIncomplete: 0,
      blendsIncomplete: 0,
      tankMaintenanceDue: 0,
      fermenterMaintenanceDue: 0,
      stillMaintenanceDue: 0,
      equipmentMaintenanceDue: 0,

      provincialOutstanding: false,
      province: null,

      days: [{text:'never',value:0},{divider:true}],
      frequency: 0,
      next: format(new Date(),'yyyy-MM-dd'),
      dirty: false
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    settings() {
      return this.$store.getters.userSettings;
    },
    outstandingSettings() {
      return {reporting: true, reportingWarningDays:10, maintenance:true, production:true, inventory:true, sales:true, purchasing:true, ...this.settings.dashboard?.outstandingSettings};
    },
    outstanding() {
      return (this.receiptsPending + this.receiptsDraft + this.poRequiringApproval + this.salesOrdersOpen + this.recurringTransactionsReminders +
              this.itemsLowInventory + this.inBondProductsLowInventory + this.dutyPaidProductsLowInventory + this.mashesIncomplete +
              this.spiritsLowInventory + this.winesLowInventory +
              this.tankMaintenanceDue + this.fermenterMaintenanceDue + this.stillMaintenanceDue + this.equipmentMaintenanceDue +
              this.distillationsIncomplete + this.blendsIncomplete + this.bottlingsIncomplete + this.assembliesIncomplete) > 0 ||
              this.exciseDue || this.provincialOutstanding;
    },
    exciseDue() {
      if (!this.permission('excise.file') || !this.outstandingSettings.reporting)
        return false;

      const nextFilingForPeriodEnd = format(lastDayOfMonth(add(new Date(),{months:-1})),'yyyy-MM-dd'); // end of last month
      if (this.company.excisePeriodEnd >= nextFilingForPeriodEnd)   // already filed
        return false;

      if (this.company.excisePeriodEnd < format(lastDayOfMonth(add(new Date(),{months:-2})),'yyyy-MM-dd'))
        return true;

      const warningDate = format(add(lastDayOfMonth(new Date()),{days:-this.outstandingSettings.reportingWarningDays}),'yyyy-MM-dd');
      if (warningDate > format(new Date(),'yyyy-MM-dd'))
        return false;

      return true;
    }
  },
  watch: {
    frequency() {this.dirty = true},
    next() {this.dirty = true}
  },
  created() {
    for (let n = 1; n <= 30; n++)
      this.days.push(n);
    if (this.outstandingSettings.reporting)
      this.province = () => import(`@/views/provinces/${this.company.province}/dashboard/Outstanding.vue`);
    this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      const po = this.permission('po.approve') && this.company.allowPO && this.outstandingSettings.purchasing;
      const inventory = this.permission('inventory') && this.outstandingSettings.inventory;
      const production = this.permission('production') && this.outstandingSettings.production;
      const maintenance = this.permission('production') && this.outstandingSettings.maintenance;
      const selling = this.permission('selling') && this.outstandingSettings.sales;
      const buying = this.permission('buying') && this.outstandingSettings.purchasing;
      const results = await this.$apiCall(`query DashboardOutstanding($inventory:Boolean!,$production:Boolean!,$selling:Boolean!,$buying:Boolean!,$po:Boolean!,$maintenance:Boolean!) {
                                                itemsLowInventory @include(if:$inventory)
                                                spiritsLowInventory @include(if:$inventory)
                                                winesLowInventory @include(if:$inventory)
                                                inBondProductsLowInventory: productsLowInventory(bonded:true) @include(if:$inventory)
                                                dutyPaidProductsLowInventory: productsLowInventory(bonded:false) @include(if:$inventory)
                                                mashesIncomplete @include(if:$production)
                                                salesOrdersOpen @include(if:$selling)
                                                distillationsIncomplete @include(if:$production)
                                                bottlingsIncomplete @include(if:$production)
                                                assembliesIncomplete @include(if:$production)
                                                blendsIncomplete @include(if:$production)
                                                shipmentsDraft @include(if:$selling)
                                                inventoryReceiptsStatus @include(if:$buying) {draft,pending}
                                                equipmentMaintenanceDue @include(if:$maintenance) {typeName}
                                                purchaseOrdersRequiringApproval @include(if:$po)
                                                recurringTransactionsReminders
                                              }`,
                                          {inventory,production,selling, buying, po, maintenance});
      this.loading = false;
      if (!results.error) {
        this.poRequiringApproval = po ? results.data.purchaseOrdersRequiringApproval : 0;
        this.salesOrdersOpen = selling ? results.data.salesOrdersOpen : 0;
        this.shipmentsDraft = selling ? results.data.shipmentsDraft : 0;
        this.receiptsPending = buying ? results.data.inventoryReceiptsStatus.pending : 0;
        this.receiptsDraft = buying ? results.data.inventoryReceiptsStatus.draft : 0;
        this.recurringTransactionsReminders = results.data.recurringTransactionsReminders;
        this.itemsLowInventory = inventory ? results.data.itemsLowInventory : 0;
        this.spiritsLowInventory = inventory ? results.data.spiritsLowInventory : 0;
        this.winesLowInventory = inventory ? results.data.winesLowInventory : 0;
        this.inBondProductsLowInventory = inventory ? results.data.inBondProductsLowInventory : 0;
        this.dutyPaidProductsLowInventory = inventory ? results.data.dutyPaidProductsLowInventory : 0;
        this.mashesIncomplete = production ? results.data.mashesIncomplete : 0;
        this.distillationsIncomplete = production ? results.data.distillationsIncomplete : 0;
        this.bottlingsIncomplete = production ? results.data.bottlingsIncomplete : 0;
        this.assembliesIncomplete = production ? results.data.assembliesIncomplete : 0;
        this.blendsIncomplete = production ? results.data.blendsIncomplete : 0;

        this.tankMaintenanceDue = 0;
        this.fermenterMaintenanceDue = 0;
        this.stillMaintenanceDue = 0;
        this.equipmentMaintenanceDue = 0;
        results.data.equipmentMaintenanceDue?.forEach(e => {
          switch (e.typeName) {
            case 'Tank':
              this.tankMaintenanceDue++;
              break;
            case 'Fermenter':
              this.fermenterMaintenanceDue++;
              break;
            case 'Still':
              this.stillMaintenanceDue++;
              break;
            case 'Other equipment':
              this.equipmentMaintenanceDue++;
              break;
          }
        });
      }
    },
    permission(access) {
      return this.company.permissions.includes(access);
    },
    async loadSettings() {
      const results = await this.$apiCall(`query DashboardOutstanding {
                                              notification {
                                                next,frequency
                                              }
                                            }`,
                                          {});
      if (!results.error) {
        this.frequency = results.data.notification.frequency;
        this.next = results.data.notification.next || this.next;
        this.$nextTick(() => this.dirty = false);
      }
    },
    async saveSettings() {
      const results = await this.$apiCall(`mutation DashboardOutstandingSettings($input:NotificationInput!) {
                                                setNotification(input:$input) {
                                                  next, frequency
                                                }
                                              }`,
                                          {input:{next:this.next,frequency:this.frequency}});
      if (!results.error)
        this.dirty = false;
    },
    saveOutstandingSettings() {
      this.settings.dashboard = {...this.settings.dashboard??{}, outstandingSettings:this.outstandingSettings};
      this.$store.commit('setUserSettings', this.settings);
      this.refresh();
    }
  }
}
</script>